import React, { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { canMoveMoney, isSuperAdmin, roleFromToken, rolesFromToken } from '../config/adminAccess';

interface AuthContextType {
    isAuthenticated: boolean;
    token: string | null;
    admin: any | null;
    /** Primary role: 'super_admin' | 'marketing' | 'ops_*' — see config/adminAccess.ts */
    role: string | null;
    /** Every role of the login (one person may run several services). Use this for access checks. */
    roles: string[];
    login: (token: string, admin: any) => void;
    logout: () => void;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType>({} as any);

export const useAuth = () => useContext(AuthContext);

/** Actions the backend keeps super_admin-only (approval, deletion, wallets, payouts) are hidden for everyone else. */
export const useIsSuperAdmin = () => isSuperAdmin(useContext(AuthContext).roles);

/** Payout approval and ledger adjustments: Super Admin and Finance. */
export const useCanMoveMoney = () => canMoveMoney(useContext(AuthContext).roles);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem('admin_token'));
    const [admin, setAdmin] = useState<any | null>(() => {
        try {
            return JSON.parse(localStorage.getItem('admin_user') || 'null');
        } catch {
            return null;
        }
    });

    // Keep Sentry user context in sync with auth state.
    // Only the ID is sent — no name, email, or phone to avoid PII in Sentry.
    useEffect(() => {
        if (admin?.id) {
            Sentry.setUser({ id: admin.id });
        } else {
            Sentry.setUser(null);
        }
    }, [admin?.id]);

    const login = (newToken: string, newAdmin: any) => {
        localStorage.setItem('admin_token', newToken);
        localStorage.setItem('admin_user', JSON.stringify(newAdmin));
        setToken(newToken);
        setAdmin(newAdmin);
        // User context is set via the useEffect above
    };

    const logout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setToken(null);
        setAdmin(null);
        // useEffect clears Sentry user when admin becomes null
    };

    // The token is the server's word on the role; admin.role (login response) is the fallback.
    const role = roleFromToken(token) ?? admin?.role ?? null;
    const tokenRoles = rolesFromToken(token);
    const roles = tokenRoles.length ? tokenRoles : (Array.isArray(admin?.roles) ? admin.roles : role ? [role] : []);

    return (
        <AuthContext.Provider value={{ isAuthenticated: !!token, token, admin, role, roles, login, logout, loading: false }}>
            {children}
        </AuthContext.Provider>
    );
};
